// Pull interview questions out of a ProseMirror doc.
//
// Interview briefs are identified by top-level `briefField` nodes. For those,
// questions come from the first enabled field whose normalised key is
// `questions`; a brief with no such field falls back to the heuristic below.
// Ordinary docs use the legacy heuristic: find the H2 whose text starts with
// "Interview questions" (case-insensitive), then collect blocks up to the
// next H2 or end of doc. In either path, each top-level list
// item / paragraph becomes one InterviewQuestion. Nested sub-bullets under a
// list item are pulled out as `hints` for the shoot view.
//
// Returns null when the relevant questions field or H2 section is absent so
// callers can hide the entry point entirely.

import { BRIEF_FIELD_NODE, normalizeBriefFieldAttrs, type BriefAudience, type BriefBodyKind } from './briefFields';
import { MAX_WARMUPS, warmupTexts } from './warmupBank';

interface PMNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: PMNode[];
  text?: string;
}

export interface InterviewQuestion {
  /** The question text itself (the first paragraph in a list item, or a
   *  standalone paragraph under the heading). */
  text: string;
  /** Optional cue points / soundbites the interviewer wants the talent to
   *  hit in their answer. Authored as indented sub-bullets in the editor.
   *  Always an array — empty when the question has no hints. */
  hints: string[];
}

function nodeText(node: PMNode | undefined): string {
  if (!node) return '';
  if (node.type === 'text') return node.text ?? '';
  if (!node.content) return '';
  return node.content.map(nodeText).join('');
}

function isInterviewHeading(node: PMNode): boolean {
  if (node.type !== 'heading') return false;
  if ((node.attrs?.level as number | undefined) !== 2) return false;
  return /^interview questions/i.test(nodeText(node).trim());
}

// A ProseMirror listItem holds a paragraph (the question) and may hold a
// nested bulletList/orderedList (the hints). Pull both out in one pass.
function listItemToQuestion(li: PMNode): InterviewQuestion | null {
  const textParts: string[] = [];
  const hints: string[] = [];
  for (const child of li.content ?? []) {
    if (child.type === 'bulletList' || child.type === 'orderedList') {
      for (const hintLi of child.content ?? []) {
        const hint = nodeText(hintLi).trim();
        if (hint) hints.push(hint);
      }
    } else {
      const t = nodeText(child).trim();
      if (t) textParts.push(t);
    }
  }
  const text = textParts.join(' ').trim();
  if (!text) return null;
  return { text, hints };
}

function questionsFromBlocks(blocks: PMNode[], stopAtH2: boolean): InterviewQuestion[] {
  const questions: InterviewQuestion[] = [];
  for (const block of blocks) {
    if (
      stopAtH2 &&
      block.type === 'heading' &&
      (block.attrs?.level as number | undefined) === 2
    ) {
      break;
    }

    if (block.type === 'bulletList' || block.type === 'orderedList') {
      for (const li of block.content ?? []) {
        const question = listItemToQuestion(li);
        if (question) questions.push(question);
      }
    } else if (block.type === 'paragraph') {
      const text = nodeText(block).trim();
      if (text) questions.push({ text, hints: [] });
    }
  }
  return questions;
}

export function extractInterviewQuestions(docJson: string, key: 'questions' | 'warmup' = 'questions'): InterviewQuestion[] | null {
  let doc: PMNode;
  try {
    doc = JSON.parse(docJson);
  } catch {
    return null;
  }
  const blocks = Array.isArray(doc?.content) ? doc.content : [];
  const isBrief = blocks.some((block) => block.type === BRIEF_FIELD_NODE);
  if (isBrief) {
    const questionsField = blocks.find((block) => {
      if (block.type !== BRIEF_FIELD_NODE) return false;
      const attrs = normalizeBriefFieldAttrs(block.attrs);
      return attrs.key === key && attrs.enabled === true;
    });
    if (questionsField) return key === 'warmup'
      ? warmupTexts(questionsField.content ?? []).slice(0, MAX_WARMUPS).map(text => ({ text, hints: [] }))
      : questionsFromBlocks(questionsField.content ?? [], false);
    // No enabled questions field: fall through to the H2 heuristic, so a brief
    // whose questions were typed as a plain "Interview questions" heading
    // between fields still feeds the shoot view. Client routes project first,
    // so this only ever widens what the admin sees.
  }

  // Warm-ups only come from their own field, never a legacy interview H2.
  if (key === 'warmup') return null;

  const startIdx = blocks.findIndex(isInterviewHeading);
  if (startIdx === -1) return null;
  return questionsFromBlocks(blocks.slice(startIdx + 1), true);
}

export function extractWarmups(docJson: string): InterviewQuestion[] | null {
  return extractInterviewQuestions(docJson, 'warmup');
}

export function hasInterviewQuestions(docJson: string): boolean {
  const qs = extractInterviewQuestions(docJson);
  return qs !== null && qs.length > 0;
}

export interface ShootField {
  id: string;
  key: string;
  title: string;
  audience: BriefAudience;
  body_kind: BriefBodyKind;
  content: unknown[];
}

/** Select only top-level fields, preserving the stored nodes and their order. */
function shootFieldNodes(docJson: string): PMNode[] {
  try {
    const doc = JSON.parse(docJson) as PMNode | null;
    if (!Array.isArray(doc?.content)) return [];
    return doc.content.filter((node) => {
      if (node?.type !== BRIEF_FIELD_NODE) return false;
      const attrs = normalizeBriefFieldAttrs(node.attrs);
      return attrs.enabled && attrs.shoot && attrs.key !== 'questions';
    });
  } catch {
    return [];
  }
}

export function extractShootFields(docJson: string): ShootField[] {
  return shootFieldNodes(docJson).map((node) => {
    const { id, key, title, audience, body_kind } = normalizeBriefFieldAttrs(node.attrs);
    return { id, key, title, audience, body_kind, content: Array.isArray(node.content) ? node.content : [] };
  });
}

export function buildShootDoc(docJson: string): { type: 'doc'; content: unknown[] } {
  return { type: 'doc', content: shootFieldNodes(docJson) };
}

/**
 * The single predicate for every shoot view entry point (editor control,
 * review header button, inline card). If they ever disagree, a client can
 * reach an empty page.
 */
export function hasShootContent(docJson: string): boolean {
  return hasInterviewQuestions(docJson) || extractShootFields(docJson).length > 0;
}
